import React, { useState, useEffect } from "react";
import "../styles/Dashboard.css";
import Header from "../components/HeaderFooter/Header";
import Footer from "../components/HeaderFooter/Footer";
import ServiceCard from "../components/serviceComponents/ServiceCard";
import ProjectCard from "../components/projectComponents/ProjectCard";
import Pagination from "../components/Pagination";
import { getServiceCalls, fetchProjectDetails } from "../services/API";

const Dashboard = () => {
  const [serviceCards, setServiceCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [projectCards, setProjectCards] = useState([]);
  const [projectLoading, setProjectLoading] = useState(true);
  const [projectError, setProjectError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const cardsPerPage = 3;

  const [currentProjectPage, setCurrentProjectPage] = useState(1);
  const projectCardsPerPage = 3;

  // Transform service call data
  const transformServiceCalls = (apiResponse) =>
    apiResponse.map((item, index) => {
      const {
        serviceData: { address, status, serviceCallId },
        task: { taskName, employeesAssigned, subtasks },
      } = item;

      const startDate = new Date(employeesAssigned[0]?.timeStart);
      const endDate = new Date(employeesAssigned[0]?.timeFinish);

      const dateRange = `${startDate.toLocaleDateString("en-US", {
        timeZone: "UTC", 
        day: "numeric",
        month: "short",
      })} - ${endDate.toLocaleDateString("en-US", {
        timeZone: "UTC", 
        day: "numeric",
        month: "short",
        year: "numeric",
      })}`;
      const timeRange = `${startDate.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })} - ${endDate.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
      const workers = employeesAssigned.map(
        (employee) => employee.employeeName
      );

      const statusImages = {
        "Not Started": "/images/notstarted.png",
        "In Progress": "/images/progress.png",
        "Completed": "/images/complete.png",
      };

      return {
        serviceCallId: serviceCallId,
        id: index + 1,
        dateRange,
        status: status.toUpperCase().replace(" ", "-"),
        address,
        time: timeRange,
        work: taskName,
        subtasks: subtasks,
        workers,
        img: statusImages[status] || "/images/default.png",
      };
    });

  // Transform project card data
  const transformProjectCards = (data) =>
    data.map((item) => {
      const projectData = item.projectData;

      const tasks = item.Tasks.map((task) => ({
        taskName: task.Task_Name,
        subtasks: task.Subtasks.map((subtask) => subtask.Subtask_Name),
      }));

      const timeFrame =
        item.Timeframe?.[0]?.["MIN(Time_Start)"] &&
        item.Timeframe?.[0]?.["MAX(Time_Finish)"]
          ? `${new Date(
              item.Timeframe[0]["MIN(Time_Start)"]
            ).toLocaleDateString("en-US", {
              timeZone: "UTC", // Handle timezone explicitly
              day: "numeric",
              month: "short",
              year: "numeric",
            })} - ${new Date(
              item.Timeframe[0]["MAX(Time_Finish)"]
            ).toLocaleDateString("en-US", {
              timeZone: "UTC", // Handle timezone explicitly
              day: "numeric",
              month: "short",
              year: "numeric",
            })}`
          : "Timeframe Not Available";

      const attachments = item.Attachments.map((attachment) => ({
        name: attachment.Attachment_Name,
        type: attachment.Attachment_Type,
        path: attachment.File_Path,
      }));

      return {
        projectId: projectData.Project_ID,
        description: projectData.Project_Description,
        address: projectData.Address,
        status: projectData.Status,
        contact: projectData.Contact_Email,
        tasks,
        timeFrame,
        attachments,
      };
    });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const serviceResponse = await getServiceCalls();
        setServiceCards(transformServiceCalls(serviceResponse.data));

        const projectResponse = await fetchProjectDetails();
        const transformedProjects = transformProjectCards(projectResponse.data);
        setProjectCards(transformedProjects);

        setProjectLoading(false);
      } catch (error) {
        setError("Failed to load service calls.");
        setProjectError("Failed to load project cards.");
      } finally {
        setLoading(false);
      }
    };  
    fetchData();
  }, []);

  // Debugging Project Cards State
  useEffect(() => {
    console.log("Updated project cards:", projectCards);
  }, [projectCards]);

  // Pagination helpers
  const paginate = (data, currentPage, itemsPerPage) => data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const serviceCardPages = Math.ceil(serviceCards.length / cardsPerPage);
  
  const projectCardPages = Math.ceil(projectCards.length / projectCardsPerPage);

  const handlePageChange = (setPage, totalPages) => (page) => { if (page > 0 && page <= totalPages) setPage(page)};

  return (
    <div className="dashboard-container">
      <Header />

      <div className="dashboard-content">
        {/* Service Calls Section */}
        <section className="service-calls">
          <h2>Service Calls</h2>
          {loading ? (
            <p>Loading service cards...</p>
          ) : error ? (
            <p>{error}</p>
          ) : (
            <div className="card-container">
              {paginate(serviceCards, currentPage, cardsPerPage).map((card) => (
                <ServiceCard key={card.id} card={card} />
              ))}
            </div>
          )}
          <Pagination currentPage={currentPage} totalPages={serviceCardPages} onPageClick={handlePageChange(setCurrentPage, serviceCardPages)}
            onNextClick={() => handlePageChange( setCurrentPage, serviceCardPages )(currentPage + 1) }
          />
        </section>

        {/* Projects Section */}
        <section className="projects">
          <h2>Projects</h2>
          {projectLoading ? (
            <p>Loading project cards...</p>
          ) : projectError ? (
            <p>{projectError}</p>
          ) : (
            <div className="card-container">
              {paginate(
                projectCards,
                currentProjectPage,
                projectCardsPerPage
              ).map((project) => (
                <ProjectCard key={project.projectId} project={project} />
              ))}
            </div>
          )}
          <Pagination
            currentPage={currentProjectPage}
            totalPages={projectCardPages}
            onPageClick={(page) => page > 0 && page <= projectCardPages && setCurrentProjectPage(page) }
            onNextClick={() => setCurrentProjectPage((prevPage) => Math.min(prevPage + 1, projectCardPages)) }
          />
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;
